// Gallery configuration interface
interface GalleryConfig {
    totalItems: number;
    itemAngle: number;
}

// Gallery state interface
interface GalleryState {
    isJumping: boolean;
    ticking: boolean;
}

// Gallery class for managing 3D carousel
class Gallery {
    private readonly galleryElement: HTMLElement;
    private readonly config: GalleryConfig;
    private readonly state: GalleryState;

    constructor(galleryId: string = 'gallery') {
        // Get gallery element with null check
        const element = document.getElementById(galleryId);
        if (!element) {
            throw new Error(`Gallery element with id "${galleryId}" not found`);
        }
        this.galleryElement = element;

        // Initialize configuration
        this.config = {
            totalItems: 12,
            itemAngle: 360 / 12 // 30度ずつ
        };

        // Initialize state
        this.state = {
            isJumping: false, // ジャンプ中のフラグ
            ticking: false
        };

        this.init();
    }

    /**
     * Initialize the gallery
     */
    private init(): void {
        this.bindEvents();
        this.updateGallery();
    }

    /**
     * Update gallery rotation based on scroll position
     */
    private updateGallery(): void {
        // スクロール量を取得（0-1の範囲）
        const scrollTop: number = window.pageYOffset;
        const docHeight: number = document.body.scrollHeight - window.innerHeight;
        const scrollPercent: number = Math.min(scrollTop / docHeight, 1);
        
        // k値を計算（-1から1の範囲、元のCodePenと同じロジック）
        const k: number = (scrollPercent * 2) - 1; // 0-1 を -1-1 に変換
        
        // 回転角度を計算（k値ベース）
        const rotationAngle: number = (k + 1) * 360; // k=-1で0度、k=1で720度
        
        // CSSカスタムプロパティを更新
        this.galleryElement.style.setProperty('--rotation', `${rotationAngle}deg`);
        
        // 正面にある画像を計算
        const normalizedAngle: number = ((rotationAngle % 360) + 360) % 360;
        const frontImageIndex: number = Math.round(normalizedAngle / this.config.itemAngle) % this.config.totalItems;
        const frontImageNumber: number = frontImageIndex + 1;

        // 無限スクロールの判定と実行
        this.checkAndPerformInfiniteScroll(k, docHeight);
    }

    /**
     * Check and perform infinite scroll
     * @param k - Current k value (-1 to 1)
     * @param maxScrollHeight - Maximum scroll height
     */
    private checkAndPerformInfiniteScroll(k: number, maxScrollHeight: number): void {
        // ジャンプ中は何もしない
        if (this.state.isJumping) return;

        // k の絶対値が 0.5 を超えた場合に無限ループを実行
        if (Math.abs(k) > 0.5) {
            this.state.isJumping = true; // ジャンプ開始

            // 元のCodePenと同じ計算式
            const newScrollPosition: number = 0.5 * (k - Math.sign(k) + 1) * maxScrollHeight;
            
            // console.log(`無限スクロール実行: k=${k.toFixed(3)}, 新しい位置=${Math.round(newScrollPosition)}`);
            
            // スクロール位置をジャンプ
            window.scrollTo(0, newScrollPosition);
            
            // 少し待ってからフラグをリセット
            setTimeout(() => {
                this.state.isJumping = false;
            }, 50);
        }
    }

    /**
     * Handle keyboard events
     * @param event - Keyboard event
     */
    private handleKeyDown = (event: KeyboardEvent): void => {
        const scrollAmount: number = window.innerHeight * 0.1; // 10%ずつスクロール
        
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                window.scrollBy(0, scrollAmount);
                break;
            case 'ArrowUp':
                event.preventDefault();
                window.scrollBy(0, -scrollAmount);
                break;
        }
    };

    /**
     * Handle scroll events with throttling
     */
    private handleScroll = (): void => {
        if (!this.state.ticking) {
            requestAnimationFrame(() => {
                this.updateGallery();
                this.state.ticking = false;
            });
            this.state.ticking = true;
        }
    };

    /**
     * Bind event listeners
     */
    private bindEvents(): void {
        // スクロールイベント（最適化版）
        window.addEventListener('scroll', this.handleScroll);

        // キーボード操作（オプション）
        document.addEventListener('keydown', this.handleKeyDown);
    }

    /**
     * Remove event listeners (cleanup)
     */
    public destroy(): void {
        window.removeEventListener('scroll', this.handleScroll);
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    /**
     * Get current gallery state (for debugging)
     */
    public getState(): Readonly<GalleryState> {
        return { ...this.state };
    }

    /**
     * Get gallery configuration (for debugging)
     */
    public getConfig(): Readonly<GalleryConfig> {
        return { ...this.config };
    }
}

// Export for use in other modules
export default Gallery;

// Initialize gallery when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    try {
        new Gallery();
    } catch (error) {
        console.error('Failed to initialize gallery:', error);
    }
});
